import { Component, h, useEffect, useMemo, useRef } from "@lukekaalim/act"
import { TheatreData, TheatreGridChunk } from "../hooks/useTheatreController"
import { a3, PropsFromClass, registry } from "@lukekaalim/act-three"
import * as three from 'three';
import { useDisposable } from "@kosmod/graphics-3d";
import { TheatreCoreResources } from "../hooks/useTheatreCoreResources";
import { useIsDebug } from "@kosmod/kit";

export const getAdjacent = (target: three.Vector2, includeSelf: boolean = false) => {
  const adjacent = [
    new three.Vector2(target.x + 1, target.y + 0),
    new three.Vector2(target.x + 1, target.y + 1),
    new three.Vector2(target.x + 0, target.y + 1),
    new three.Vector2(target.x - 1, target.y + 1),
    new three.Vector2(target.x - 1, target.y + 0),
    new three.Vector2(target.x - 1, target.y - 1),
    new three.Vector2(target.x + 0, target.y - 1),
    new three.Vector2(target.x + 1, target.y - 1),
  ]
  if (includeSelf)
    adjacent.push(target.clone())
  return adjacent;
}

const writeGridGeometry = (read: (x: number, y: number) => boolean, size: three.Vector2) => {
  const positions: number[] = [];
  const indices: number[] = [];
  const uvs: number[] = [];

  const pushTileQuad = (x: number, y: number) => {
    const positionIndex = positions.length / 3;
    positions.push(
      x,     0 , y,
      x + 1, 0 , y,
      x,     0 , y + 1,
      x + 1, 0 , y + 1,
    )
    uvs.push(
      0.5   , 1,
      0.5,    0.5,
      0   , 1,
      0,    0.5
    )
    indices.push(
      positionIndex + 2, positionIndex + 1, positionIndex,
      positionIndex + 1, positionIndex + 2, positionIndex + 3
    )
  }

  for (let y = 0; y < size.y; y++) {
    for (let x = 0; x < size.x; x++) {
      const hasFloor = read(x, y)
      if (hasFloor) {
        pushTileQuad(x, y);
      } else {

        const pushEdgeQuad = (quadrant: number) => {
          const leftOffset = visitOrder[quadrant];
          const rightOffset = visitOrder[(quadrant + 1) % 4]

          const left = read(x + leftOffset.x, y + leftOffset.y);
          const right = read(x + rightOffset.x, y + rightOffset.y);
          const opposite = read(x + leftOffset.x + rightOffset.x, y + leftOffset.y + rightOffset.y)

          if (!left && !right && !opposite)
            return;

          const positionIndex = positions.length / 3;
          const positionOffset = quadrantOffsets[quadrant];
          positions.push(
            x + 0   + positionOffset.x, 0, y + 0.5 + positionOffset.y,
            x + 0.5 + positionOffset.x, 0, y + 0.5 + positionOffset.y,
            x + 0   + positionOffset.x, 0, y + 1 + positionOffset.y,
            x + 0.5 + positionOffset.x, 0, y + 1 + positionOffset.y,
          )
          indices.push(
            positionIndex + 2, positionIndex + 1, positionIndex,
            positionIndex + 1, positionIndex + 2, positionIndex + 3
          )
          if (left && !right) {
            uvs.push(...rotateUV([
              0.75, 1.0,
              0.75, 0.75,
              0.50, 1.0,
              0.50, 0.75
            ], quadrant + 2))
          }
          else if (!left && right) {
            uvs.push(...rotateUV([
              0.75, 1.0,
              0.75, 0.75,
              0.50, 1.0,
              0.50, 0.75
            ], quadrant + 3))
          }
          else if (right && left) {
            uvs.push(...rotateUV([
              1.00, 1,
              1.00, 0.75,
              0.75, 1,
              0.75, 0.75
            ], quadrant + 2))
          }
          else if (opposite) {
            uvs.push(...rotateUV([
              1.00, 0.75,
              1.00, 0.50,
              0.75, 0.75,
              0.75, 0.50
            ], quadrant + 2))
          }
        }

        const rotateUV = (coords: number[], rotations: number = 1) => {
          if (rotations < 1)
            return coords;

          const newUvs = [
            coords[4], coords[5],
            coords[0], coords[1],
            coords[6], coords[7],
            coords[2], coords[3],
          ];
          return rotateUV(newUvs, rotations - 1);
        }
        const visitOrder = [
          new three.Vector2(1, 0),
          new three.Vector2(0, 1),
          new three.Vector2(-1, 0),
          new three.Vector2(0, -1),
        ]
        const quadrantOffsets = [
          new three.Vector2(0.5, 0),
          new three.Vector2(0, 0),
          new three.Vector2(0, -0.5),
          new three.Vector2(0.5, -0.5),
        ]

        for (let quadrant = 0; quadrant < 4; quadrant++) {
          pushEdgeQuad(quadrant);
        }
      }
    }
  }

  return { uvs, positions, indices };
}

export type GridChunkRenderer = {
  position: three.Vector2,
  size: three.Vector2,
  data: TheatreData,
  res: TheatreCoreResources,

  color?: three.Color
}

export const GridChunkRenderer: Component<GridChunkRenderer> = ({ position, size, data, res, color = new three.Color('yellow') }) => {
  const geometry = useDisposable(() => new three.BufferGeometry(), []);

  const versions = getAdjacent(position, true).map(position => {
    const chunk = data.grids.get(`${position.x},${position.y}`);
    if (!chunk)
      return -1;
    return chunk.version;
  })

  useEffect(() => {
    const read = (x: number, y: number) => {
      
      const chunkOffset = new three.Vector2(Math.floor(x / 8), Math.floor(y / 8));
      const readChunkPosition = new three.Vector2().copy(chunkOffset).add(position);

      const readChunk = data.grids.get(`${readChunkPosition.x},${readChunkPosition.y}`);
      if (!readChunk)
        return false;

      const localOffset = new three.Vector2(x - (chunkOffset.x * 8), y - (chunkOffset.y * 8));
      return readChunk.gridData[localOffset.y][localOffset.x];
    }

    const { indices, positions, uvs } = writeGridGeometry(read, size)

    geometry.setIndex(indices)
    geometry.setAttribute('position', new three.BufferAttribute(new Float32Array(positions), 3))
    geometry.setAttribute('uv', new three.BufferAttribute(new Float32Array(uvs), 2))

    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
    
    if (boundsHelperRef.get())
      (boundsHelperRef.get() as three.BoxHelper).setFromObject((ref.get() as three.Mesh));
  }, [geometry, ...versions]);

  const boundsHelperRef = useRef<three.BoxHelper | null>(null);
  const ref = useRef<three.Mesh | null>(null);
  const IS_DEBUG = useIsDebug();

  return [
    IS_DEBUG && [
      h(a3.Box3Helper, {
        position: { x: position.x * 8, y: 0, z: position.y * 8 },
        color,
        box: new three.Box3(
          new three.Vector3((position.x * 8) + 0.02, 0, (position.y * 8) + 0.02),
          new three.Vector3((position.x * 8) + size.x - 0.02, 0, (position.y * 8) + size.y - 0.02),
        )
      }),
      h(a3.BoxHelper, {
        ref: boundsHelperRef,
        material: new three.LineBasicMaterial({ color: 'pink' })
      }),
    ],
    h(a3.mesh, {
      ref,
      position: { x: position.x * 8, y: 0, z: position.y * 8 },
      material: new three.MeshBasicMaterial({ map: res.gridTileMap, transparent: true }),
      geometry
    }),
  ]
}

declare module "@lukekaalim/act-three" {
  interface ExtendedPrimitives {
    BoxHelper: PropsFromClass<three.BoxHelper>,
  }
}
registry.registerPrimitive('BoxHelper', () => new three.BoxHelper((undefined as any)), () => {})